import _ from "lodash";
import PropTypes from "prop-types";
import { Button, createTheme, ThemeProvider } from "@mui/material";
import baseTheme from "../themes/baseTheme";

const grey = baseTheme.palette.grey;
const blue = baseTheme.palette.blue;
const green = baseTheme.palette.green;
const orange = baseTheme.palette.orange;
const red = baseTheme.palette.red;

const defaultStyles = {
  padding: "0.25rem 0.6rem",
  fontSize: "0.9rem",
  height: "2.2rem",
  lineHeight: "1.25",
  minWidth: "1.2rem",
  border: "solid 1px",
  fontFamily: "Inter Medium",
  textTransform: "none",
  "& svg + span": {
    marginLeft: "0.6rem",
  },
  "& span + svg": {
    marginLeft: "0.6rem",
  },
  "&.icon-only": {
    padding: "0.25rem 0.6rem",
  },
};

const sizes = {
  default: defaultStyles,
  small: _.merge(_.cloneDeep(defaultStyles), {
    padding: "0.15rem 0.5rem",
    height: "2rem",
    "&.icon-only": {
      padding: "0.5rem",
    },
  }),
  large: _.merge(_.cloneDeep(defaultStyles), {
    padding: "0.5rem 0.8rem",
    height: "2.5rem",
    fontSize: "1.2rem",
    lineHeight: "1.5",
    border: "solid 1px",
    "&.icon-only": {
      padding: "0.8rem",
    },
  }),
};

const roles = {
  default: {
    color: grey[0],
    backgroundColor: blue[50],
    borderColor: blue[50],
    ":hover": {
      backgroundColor: blue[60],
      borderColor: blue[60],
    },
    "&:disabled": {
      color: grey[0],
      backgroundColor: blue[40],
      borderColor: blue[40],
    },
    "& svg": {
      stroke: grey[0],
    },
  },
  secondary: {
    color: blue[50],
    backgroundColor: blue[30],
    borderColor: blue[30],
    ":hover": {
      backgroundColor: blue[40],
      borderColor: blue[40],
    },
    "&:disabled": {
      color: blue[40],
      backgroundColor: blue[15],
      borderColor: blue[15],
      "& svg": {
        stroke: blue[40],
      },
    },
    "& svg": {
      stroke: blue[50],
    },
  },
  tertiary: {
    color: grey[100],
    backgroundColor: grey[0],
    borderColor: grey[30],
    ":hover": {
      backgroundColor: grey[20],
      borderColor: grey[30],
    },
    "&:disabled": {
      color: grey[50],
      backgroundColor: grey[10],
      borderColor: grey[10],
    },
    "& svg": {
      stroke: grey[80],
    },
  },
  linkGrey: {
    height: "2rem",
    padding: "0px",
    color: grey[80],
    backgroundColor: "transparent",
    borderColor: "transparent",
    ":hover": {
      color: grey[90],
      backgroundColor: "transparent",
      borderColor: "transparent",
      "& svg": {
        stroke: grey[90],
      },
    },
    "&:disabled": {
      color: grey[50],
      backgroundColor: "transparent",
      borderColor: "transparent",
      "& svg": {
        stroke: grey[50],
      },
    },
    "& svg": {
      stroke: grey[80],
    },
  },
  linkBlue: {
    height: "2rem",
    padding: "0px",
    color: blue[50],
    backgroundColor: "transparent",
    borderColor: "transparent",
    ":hover": {
      color: blue[60],
      backgroundColor: "transparent",
      borderColor: "transparent",
      "& svg": {
        stroke: blue[60],
      },
    },
    "&:disabled": {
      color: grey[50],
      backgroundColor: "transparent",
      borderColor: "transparent",
      "& svg": {
        stroke: grey[50],
      },
    },
    "& svg": {
      stroke: blue[50],
    },
  },
};

function getTheme(size, role) {
  const sizeTheme = _.cloneDeep(sizes[size] || sizes["default"]);
  const roleTheme = _.cloneDeep(roles[role] || roles["default"]);
  return createTheme({
    components: {
      MuiButton: {
        styleOverrides: {
          root: _.merge(sizeTheme, roleTheme),
        },
      },
    },
  });
}

export default function ({
  label,
  size,
  role,
  variant,
  Icon,
  iconWidth = "0.9rem",
  disabled = false,
  ...props
}) {
  return (
    <ThemeProvider theme={getTheme(size, role)}>
      <Button
        disabled={disabled}
        {...props}
        className={variant === "iconOnly" ? "icon-only" : ""}
      >
        {!Icon ? (
          <>{label}</>
        ) : variant === "leadingIcon" ? (
          <>
            <Icon width={iconWidth} />
            <span>{label}</span>
          </>
        ) : variant === "trailingIcon" ? (
          <>
            <span>{label}</span>
            <Icon width={iconWidth} />
          </>
        ) : variant === "iconOnly" ? (
          <Icon width={iconWidth} />
        ) : (
          <></>
        )}
      </Button>
    </ThemeProvider>
  );
}

Button.propTypes = {
  /**
   * Button label
   */
  label: PropTypes.string,
  /**
   * Button size
   */
  size: PropTypes.string,
  /**
   * Button variant: '' or 'leadingIcon' or 'trailingIcon' or 'iconOnly'
   */
  variant: PropTypes.string,
  /**
   * Icon: Component name
   */
  icon: PropTypes.func,
  /**
   * Icon width in px
   */
  iconWidth: PropTypes.string,
  /**
   * Optional click handler
   */
  onClick: PropTypes.func,
};

Button.defaultProps = {
  size: "default",
  role: "default",
  disabled: false,
  // onClick: undefined,
};
